import type * as ClassTypes from './types/class';
import {
    initAdvantages,
    initAlternativeProgressPopup,
    initAutoPlayVideo,
    initCallbackPopup,
    initFooter,
    initForm,
    initFullVideo,
    initHeader,
    initInfoPoint,
    initMobileMenu,
    initPopup,
    initScrollSlider,
    initTabs,
    initVideo,
    initYoutubePlayer,
    lazyLoadElements,
    stepBack
} from './common';
import {copyUrl} from '@/components/copy-url';
import {EBreakpoint} from '@/common/scripts/constants';
import {Genplan} from '@/sections/genplan';
import type {IDynamicImport} from './types/utils';
import type {IObserver} from '@/common/scripts/types/observer';
import Observer from './observer';
import {ProjectMap} from '@/sections/projects-map/projectsMap';
import searchPanelInit from '@/components/search-panel';
import {useAddToFavoriteButton} from '@/components/favorite/useAddToFavoriteButton';
import {useNewsList} from '@/sections/news-list';
import {useScrollOverlap} from '@/common/scripts/overlap';

const observer: IObserver = new Observer();

// Поведение шапки
const headerWrap = document.querySelector('.j-header') as HTMLElement;

if (headerWrap) {
    initHeader(headerWrap);
}

// Подключение панели параметрического поиска
const searchPanelId = '#search-panel';
const searchPanelWrapper = document.querySelector(searchPanelId);

if (searchPanelWrapper) {
    searchPanelInit({
        id     : searchPanelId,
        wrapper: searchPanelWrapper
    });
}

// Поведение меню
const burgerMenuSelectors = [...document.querySelectorAll('.j-burger')] as HTMLElement[];

if (burgerMenuSelectors.length && headerWrap) {
    initMobileMenu(burgerMenuSelectors, headerWrap);
}

// Cкролл по якорям
const anchors = [...document.querySelectorAll('.j-anchor')] as HTMLElement[];

if (anchors.length) {
    import(/* webpackChunkName: "scrollBy" */ '@/components/scrollBy')
        .then(({default: ScrollBy}: IDynamicImport) => {
            const scrollBy: ClassTypes.IScrollBy = new ScrollBy();
            const CLASS_ACTIVE = 'is-active';

            scrollBy.init({
                takeElements: [headerWrap],
                anchors
            });

            const setActiveAnchor = (id: string): void => {
                anchors.forEach((item: HTMLElement) => {
                    const href = item.getAttribute('href');

                    item.classList.remove(CLASS_ACTIVE);

                    if (href === id || href === `/${id}`) {
                        item.classList.add(CLASS_ACTIVE);
                    }
                });
            };

            const hash = window.location.hash;

            if (hash.length) {
                setActiveAnchor(hash);
            }

            observer.subscribe('anchorChangeSection', (id: string) => {
                setActiveAnchor(id);
            });
        });
}

// Видео на первом экране
const fullIframes = [...document.querySelectorAll('.j-first-screen__iframe')] as HTMLElement[];

fullIframes.forEach((fullIframe: HTMLElement) => {
    initFullVideo(fullIframe);
});

// Видео youtube на первом экране (инициализация плеера через API youtube)
const youtubeFrame = document.querySelector('.j-first-screen__youtube') as HTMLElement;

if (youtubeFrame) {
    initFullVideo(youtubeFrame);
    initYoutubePlayer(youtubeFrame);
}

/* Ленивая загрузка */
/* подключать после инициализации слайдеров */
const lazyElements = [...document.querySelectorAll('[loading=lazy]')];

if (lazyElements.length) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            lazyLoadElements(lazyElements);
        });
    } else {
        lazyLoadElements(lazyElements);
    }
}

observer.subscribe('slider:ready', (object: ClassTypes.ISlider) => {
    const sliderLazyElements = [...object.slider.querySelectorAll('[loading=lazy]')];

    lazyLoadElements(sliderLazyElements);

    if (object?.slider?.classList.contains('j-slider-finishing')) {
        const scrollSliderWrapper = [...document.querySelectorAll('.j-scroll-slider-wrapper')];

        scrollSliderWrapper.forEach((wrapper: Element) => {
            initScrollSlider(wrapper as HTMLElement);
        });

        const infoPoints = [...document.querySelectorAll<HTMLElement>('.j-info-point')];

        infoPoints.forEach((point: HTMLElement) => {
            initInfoPoint(point);
        });

        const popups = [...object.slider.querySelectorAll<HTMLElement>('.j-popup')];

        if (popups.length) {
            initPopup(popups);
        }
    }
});

// Плавный подскролл по якорям
const simpleAnchors = [...document.querySelectorAll(`a[href^="#"]:not(.j-anchor)`)];

if (simpleAnchors.length) {
    simpleAnchors.forEach((anchor: Element) => {
        const anchorHref = anchor.getAttribute('href');

        if (!anchorHref || anchorHref?.length === 1) {
            return;
        }

        const anchorBlock = document.querySelector(anchorHref);

        if (anchorBlock) {
            (anchor as HTMLElement).addEventListener('click', (event: MouseEvent) => {
                event.preventDefault();

                anchorBlock.scrollIntoView({
                    behavior: 'smooth'
                });
            });
        }
    });
}

// Табы
const tabs = [...document.querySelectorAll('.j-tabs-wrap')];

tabs.forEach((elem: Element) => {
    initTabs(elem as HTMLElement);
});

// Формы
const formWrappers = [...document.querySelectorAll('.j-form')];

formWrappers.forEach((wrapper: Element) => {
    initForm(wrapper as HTMLElement);
});

// Попапы
const popupSelectors = [...document.querySelectorAll('.j-popup')] as HTMLElement[];

if (popupSelectors.length) {
    initPopup(popupSelectors, {removeDelay: 300});
}

// Отдельный селектор для попапов обратных звонков
const popupFacadeLinks = [...document.querySelectorAll('.j-popup-callback:not([data-init])')] as HTMLElement[];

if (popupFacadeLinks.length) {
    initCallbackPopup(popupFacadeLinks);

    popupFacadeLinks.forEach((button: HTMLElement) => {
        button.dataset['init'] = 'true';
    });
}

// Отдельный селектор для попапа с картой
const projectMapPopupLinks = [...document.querySelectorAll('.j-popup-project-map:not([data-init])')] as HTMLElement[];

if (projectMapPopupLinks.length) {
    initPopup(projectMapPopupLinks);

    projectMapPopupLinks.forEach((button: HTMLElement) => {
        button.dataset['init'] = 'true';
    });
}


// Шаг назад
const stepBackSelector = document.querySelector('.j-back') as HTMLElement;

if (stepBackSelector) {
    stepBack(stepBackSelector);
}

// Уведомление об использовании cookies
const cookiesWrapper = document.querySelector('.j-cookies-notice') as HTMLElement;

if (cookiesWrapper) {
    import(/* webpackChunkName: "cookies-notice" */ '@/components/cookies-notice')
        .then(({default: CookiesNotice}: IDynamicImport) => {
            const cookiesNotice: ClassTypes.ICookiesNotice = new CookiesNotice();

            cookiesNotice.init(cookiesWrapper);
        });
}

const advantagesBlocks = [...document.querySelectorAll<HTMLElement>('.j-advantages')];

advantagesBlocks.forEach((wrapper: HTMLElement) => {
    initAdvantages(wrapper);
});

const favoriteButtons = [...document.querySelectorAll<HTMLElement>('.j-favorite-btn')];

favoriteButtons.forEach((favoriteButton: HTMLElement) => {
    useAddToFavoriteButton(favoriteButton);
});

const setVH = (): void => {
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    let vh = window.innerHeight * 0.01;

    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    document.documentElement.style.setProperty('--svh', `${vh}px`);

    // We listen to the resize event
    window.addEventListener('resize', () => {
        // We execute the same script as before
        vh = window.innerHeight * 0.01;

        document.documentElement.style.setProperty('--vh', `${vh}px`);
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        setVH();
    });
} else {
    setVH();
}

const progressAPP = document.getElementById('progress');

if (progressAPP) {
    // @ts-ignore
    document.addEventListener('progressData', (data: CustomEvent) => {
        initAlternativeProgressPopup(data.detail.cards);
    });
}

const videoPlayers = [...document.querySelectorAll<HTMLButtonElement>('.j-video-wrapper')];

if (videoPlayers.length) {
    videoPlayers.forEach((wrapper: HTMLElement) => {
        initVideo(wrapper);
    });
}

const autoPlayVideoPlayers = [...document.querySelectorAll<HTMLButtonElement>('.j-video-autoplay')];

if (autoPlayVideoPlayers.length) {
    autoPlayVideoPlayers.forEach((wrapper: HTMLElement) => {
        initAutoPlayVideo(wrapper);
    });
}

const footer = document.querySelector('.j-footer') as HTMLElement;

if (footer) {
    initFooter(footer);
}

// Подключение галерей новостей
const slidersNews = [...document.querySelectorAll('.j-slider-news')] as HTMLElement[];

if (slidersNews.length) {
    import(/* webpackChunkName: "slider" */ '@/components/slider')
        .then(({default: Slider}: IDynamicImport) => {
            slidersNews.forEach((item: HTMLElement) => {
                const sliderNews: ClassTypes.ISlider = new Slider();
                const slidesShow: Record<number, { slidesPerView: number }> = {
                    320: {
                        slidesPerView: 1
                    },
                    670: {
                        slidesPerView: 2
                    }
                };

                sliderNews.init({
                    slider         : item,
                    hasArrowButtons: true,
                    swiperOptions  : {
                        breakpoints: slidesShow,
                        loop       : true
                    }
                });
            });
        });
}

// Эфект оверлапа
const overlapBlocks = [...document.querySelectorAll('.j-overlap-main')] as HTMLElement[];

if (overlapBlocks.length) {
    overlapBlocks.forEach((block: HTMLElement) => {
        useScrollOverlap(block);
    });
}

// Переключение проектов со списка на карту на странице проектов
const projectsMap = document.querySelector<HTMLElement>('.j-project-map-section');

if (projectsMap) {
    /* eslint-disable */
    new ProjectMap(projectsMap);
}

// Подключение свайпера особенностей проекта
const slidersProjectSpecifics = [...document.querySelectorAll('.j-slider-project-specifics')] as HTMLElement[];

if (slidersProjectSpecifics.length) {
    import(/* webpackChunkName: "slider" */ '@/components/slider')
    .then(({default: Slider}: IDynamicImport) => {
        slidersProjectSpecifics.forEach((item: HTMLElement) => {
            const sliderProjectSpecifics: ClassTypes.ISlider = new Slider();
            sliderProjectSpecifics.init({
                slider: item,
                hasArrowButtons: true,
                swiperOptions: {
                    breakpoints: {
                        670: {
                            slidesPerView: 1
                        },
                        960: {
                            slidesPerView: 2
                        }
                    },
                    loop: true
                }
            });
        });
    });
}

// Подключение свайпера хода строительства
const slidersProgress = [...document.querySelectorAll('.j-slider-progress')] as HTMLElement[];

if (slidersProgress.length) {
    import(/* webpackChunkName: "slider" */ '@/components/slider')
    .then(({default: Slider}: IDynamicImport) => {
        slidersProgress.forEach((item: HTMLElement) => {
            const sliderProgress: ClassTypes.ISlider = new Slider();
            sliderProgress.init({
                slider: item,
                hasArrowButtons: true,
                swiperOptions: {
                    breakpoints: {
                        360: {
                            slidesPerView: 1
                        },
                        670: {
                            slidesPerView: 2
                        },
                        960: {
                            slidesPerView: 3
                        },
                        1440: {
                            slidesPerView: 4
                        }
                    },
                    loop: true
                }
            });
        });
    });
}

const projectGenplan = document.querySelector<HTMLElement>('.j-genplan');

if (projectGenplan) {
    /* eslint-disable */
    new Genplan(projectGenplan);
}

// Инициализация кнопок копирования ссылки адресной строки
const copyUrlButtons = [...document.querySelectorAll<HTMLElement>('.j-copy-url')];

copyUrlButtons.forEach((button: HTMLElement) => {
    copyUrl(button);
});

// Анимация оверлапа блока "о проекте" на странице проекта
const projectPageAboutSection = document.querySelector<HTMLElement>('.j-project-about')

if (projectPageAboutSection) {
    const lastEl = document.querySelector<HTMLElement>('.j-project-overlap-second')!;
    const mobileOverlapEl = document.querySelector<HTMLElement>('.j-project-about-main')!;

    if (window.innerWidth < EBreakpoint.TABLET) {
        useScrollOverlap(mobileOverlapEl, lastEl)
    } else {
        useScrollOverlap(projectPageAboutSection, lastEl);
    }
}

// Подключение свайпера в херо блоке на странице помещения
const roomHeroSwiper = [...document.querySelectorAll<HTMLElement>('.j-room-swiper')];

if (roomHeroSwiper.length) {
    import(/* webpackChunkName: "slider" */ '@/components/slider')
    .then(({default: Slider}: IDynamicImport) => {
        roomHeroSwiper.forEach((item: HTMLElement) => {
            const sliderRoomHero: ClassTypes.ISlider = new Slider();
            const slides = item.querySelectorAll('.swiper-slide');

            sliderRoomHero.init({
                slider: item,
                swiperOptions: {
                    slidesPerView: 1,
                    spaceBetween: 32,
                    loop: true,
                    pagination: {
                        el: document.querySelector('.j-room-swiper-pagination'),
                        clickable: true,
                        renderBullet: function (index: number, className: string) {
                            const bulletText = slides[index]?.getAttribute('data-bullet-text');
                            return `<span class="room-slider__bullet ${className}">${bulletText}</span>`;
                        },
                    },
                }
            });
        });
    });
}

// Переключение типа цены на странице помещения
const roomPriceSwitch = document.querySelector('.j-room-price-switch');

if (roomPriceSwitch) {
    const priceEl = document.querySelector('.j-room-price') as HTMLElement;

    roomPriceSwitch.addEventListener('change', ()=> {
        priceEl.classList.toggle('show-rent');
    });
}

// ajax подгрузка новостей на странице новостей
const newsList = document.querySelector<HTMLElement>('.j-news-list');

if (newsList) {
    useNewsList(newsList);
}
